import {z} from 'zod'
import {captureException} from '@sentry/react'
import axios, {parseAxiosPromise} from '@/axiosInstance.ts'
import {PaginationParams, PaginationResponse} from '@/types'
import {SiarBulkExportType, SiarExport, SiarExportStatus} from '@/features/projects/siar-bulk-export/types'

/* Create */
export const HttpCreateSiarBulkExportPayload = z.object({
    exportType: SiarBulkExportType,
    dateFrom: z.string().optional(),
    dateTo: z.string().optional()
})
export type HttpCreateSiarBulkExportPayload = z.infer<typeof HttpCreateSiarBulkExportPayload>

export const ConflictingExport = z.object({
    id: z.string(),
    exportType: SiarBulkExportType,
    dateFrom: z.string().nullable(),
    dateTo: z.string().nullable(),
    status: z.enum(['PENDING', 'TRANSMITTED'])
})
export type ConflictingExport = z.infer<typeof ConflictingExport>

export const DuplicateExportError = z.object({
    code: z.literal('DUPLICATE_EXPORT'),
    message: z.string(),
    conflictingExport: ConflictingExport
})
export type DuplicateExportError = z.infer<typeof DuplicateExportError>

export const httpCreateSiarBulkExport = ({payload}: {payload: HttpCreateSiarBulkExportPayload}): Promise<Blob> =>
    axios.post('v1/siar-xml-exports', payload, {responseType: 'blob'}).then(response => response.data)

/* List */
export type HttpGetSiarExportHistoryParams = PaginationParams & {
    exportType?: SiarBulkExportType
    dateFrom?: string
    dateTo?: string
    status?: SiarExportStatus
}

export const HttpGetSiarExportHistoryResponse = z.object({
    data: z.array(SiarExport),
    meta: PaginationResponse
})
export type HttpGetSiarExportHistoryResponse = z.infer<typeof HttpGetSiarExportHistoryResponse>

export const httpGetSiarExportHistory = (params: HttpGetSiarExportHistoryParams) =>
    parseAxiosPromise({
        axiosPromise: axios.get('v1/siar-xml-exports', {params}),
        responseShape: HttpGetSiarExportHistoryResponse,
        onZodError: captureException
    })

/* Download */
export const httpReDownloadSiarExport = ({id}: {id: string}): Promise<Blob> =>
    axios.get(`v1/siar-xml-exports/${id}/download`, {responseType: 'blob'}).then(response => response.data)

/* Transmit */
export const HttpConfirmTransmissionPayload = z.object({
    id: z.string(),
    transmissionDate: z.string()
})
export type HttpConfirmTransmissionPayload = z.infer<typeof HttpConfirmTransmissionPayload>

export const httpConfirmSiarExportTransmission = ({
    id,
    transmissionDate
}: HttpConfirmTransmissionPayload): Promise<void> =>
    axios.post(`v1/siar-xml-exports/${id}/transmit`, {transmissionDate}).then(() => undefined)

/* Cancel */
export const httpCancelSiarExport = ({id}: {id: string}): Promise<void> =>
    axios.delete(`v1/siar-xml-exports/${id}`).then(() => undefined)
